const stripControlChars = (text) => {
  let result = '';

  for (const char of text) {
    const code = char.charCodeAt(0);

    if (code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127)) {
      result += char;
    }
  }

  return result;
};

const DANGEROUS_PROTOCOL = /^(?:javascript|data|vbscript):/i;
const EXPLICIT_PROTOCOL = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/gi;
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const PHONE_PATTERN = /^\+?[\d\s()-]{10,20}$/;

/** Верхняя граница для денежных и количественных полей диагностики. */
export const DIAGNOSTIC_NUMBER_MAX = 1e12;

export const BRIEF_FIELD_LIMITS = {
  name: 120,
  niche: 160,
  product: 2000,
  usp: 1200,
  url: 2048,
  city: 160,
  age: 40,
  interests: 2000,
  pain: 1200,
  triggers: 1200,
  objections: 600,
  worked: 1200,
  failed: 1200,
  maxCpl: 80,
  monthlyTarget: 120,
  averageCheck: 40,
  margin: 40,
  contact: 120,
  extra: 2000,
};

export const BRIEF_LABEL_FIELD_KEYS = {
  NAME: 'name',
  NICHE: 'niche',
  PRODUCT: 'product',
  USP: 'usp',
  URL: 'url',
  CITY: 'city',
  AGE: 'age',
  INTERESTS: 'interests',
  PAIN: 'pain',
  TRIGGERS: 'triggers',
  OBJECTIONS: 'objections',
  WORKED: 'worked',
  FAILED: 'failed',
  MAX_CPL: 'maxCpl',
  MONTHLY_TARGET: 'monthlyTarget',
  CONTACT: 'contact',
  EXTRA: 'extra',
};

export const MEETING_FIELD_LIMITS = {
  name: 120,
  company: 120,
  contact: 160,
  message: 2000,
};

export const REGISTRATION_FIELD_LIMITS = {
  name: 80,
  email: 160,
  phone: 32,
};

export const AI_TEXT_LIMITS = {
  summary: 1200,
  stepTitle: 120,
  stepText: 500,
  risk: 400,
};

export const sanitizePlainTextInput = (value, maxLength = 2000) => {
  if (value == null) {
    return '';
  }

  let text = stripControlChars(String(value));
  text = text.replace(/[<>]/g, '');
  text = text.replace(/\uFEFF/g, '');

  if (text.length > maxLength) {
    text = text.slice(0, maxLength);
  }

  return text;
};

/** Убирает HTML-теги и опасные символы из текста модели / пользователя. */
export const sanitizeDisplayText = (value, maxLength = 2000) => {
  let text = sanitizePlainTextInput(value, maxLength);
  text = text.replace(HTML_TAG_PATTERN, '');
  text = text.replace(/["'`]/g, '');
  return text.trim();
};

export const sanitizeUrlInput = (value, maxLength = BRIEF_FIELD_LIMITS.url) => {
  const text = sanitizePlainTextInput(value, maxLength).trim();

  if (!text) {
    return '';
  }

  const normalized = text.replace(/\s/g, '');

  if (DANGEROUS_PROTOCOL.test(normalized)) {
    return '';
  }

  if (EXPLICIT_PROTOCOL.test(normalized)) {
    try {
      const url = new URL(text);

      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return '';
      }

      return url.toString();
    } catch {
      return '';
    }
  }

  return text;
};

export const sanitizeBriefField = (field, value) => {
  const maxLength = BRIEF_FIELD_LIMITS[field] ?? 500;

  if (field === 'url') {
    return sanitizeUrlInput(value, maxLength);
  }

  return sanitizePlainTextInput(value, maxLength);
};

export const sanitizeMeetingField = (field, value) => {
  const maxLength = MEETING_FIELD_LIMITS[field] ?? 500;
  return sanitizePlainTextInput(value, maxLength);
};

export const sanitizeRegistrationField = (field, value) => {
  const maxLength = REGISTRATION_FIELD_LIMITS[field] ?? 80;
  let text = sanitizePlainTextInput(value, maxLength);

  if (field === 'email') {
    text = text.replace(/\s/g, '').toLowerCase();
  }

  if (field === 'phone') {
    text = text.replace(/[^\d+\s()-]/g, '');
  }

  if (field === 'name') {
    text = text.replace(/[^\p{L}\p{N}\s.'-]/gu, '');
  }

  return text;
};

export const isValidRegistrationEmail = (email) => EMAIL_PATTERN.test(String(email || '').trim());

export const isValidRegistrationPhone = (phone) => PHONE_PATTERN.test(String(phone || '').trim());

export const sanitizeRegistrationPayload = (values = {}) => ({
  name: sanitizeRegistrationField('name', values.name).trim(),
  email: sanitizeRegistrationField('email', values.email).trim(),
  phone: sanitizeRegistrationField('phone', values.phone).trim(),
});

/** Число из пользовательского ввода с жёстким потолком. */
export const sanitizeFiniteNumber = (
  value,
  { min = 0, max = DIAGNOSTIC_NUMBER_MAX, fallback = 0 } = {},
) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

export const sanitizeAiReport = (report) => {
  if (!report || typeof report !== 'object') return null;

  const summary = sanitizeDisplayText(report.summary, AI_TEXT_LIMITS.summary);
  if (!summary) return null;

  const steps = Array.isArray(report.steps)
    ? report.steps
        .slice(0, 5)
        .map((step) => ({
          title: sanitizeDisplayText(step && step.title, AI_TEXT_LIMITS.stepTitle),
          text: sanitizeDisplayText(step && step.text, AI_TEXT_LIMITS.stepText),
        }))
        .filter((step) => step.title && step.text)
    : [];

  if (!steps.length) return null;

  const risks = Array.isArray(report.risks)
    ? report.risks
        .slice(0, 4)
        .map((risk) => sanitizeDisplayText(risk, AI_TEXT_LIMITS.risk))
        .filter(Boolean)
    : [];

  return {
    summary,
    steps,
    risks,
    fallback: Boolean(report.fallback),
  };
};

export const sanitizeDownloadFileName = (value, fallback = 'brief-targetolog') => {
  const base = sanitizePlainTextInput(value, 80)
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');

  return base || fallback;
};
